import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';
import type { EtatExpedition } from '../components/notifications/BandeauExpedition';

export type CanalNotification = 'EMAIL' | 'SMS' | 'IN_APP';
export type IssueEnvoi = 'JOURNALISE' | 'ENVOYE' | 'ECHEC';

export type CibleRelance =
  | 'PIECES_MANQUANTES'
  | 'DOSSIER_INCOMPLET'
  | 'ENTRETIEN_A_VENIR'
  | 'IMMERSION_SANS_RETOUR';

/** Qui reçoit la relance : la cible du scénario, le responsable du dossier, ou les deux. */
export type DestinatairesRelance = 'CIBLE' | 'RESPONSABLE' | 'LES_DEUX';
export type PourEnvoiRelance = 'CIBLE' | 'RESPONSABLE';

/**
 * Cadence d'un scénario, sous la forme propre à sa nature. Le serveur la
 * valide et l'énonce en clair (`declencheur`) : l'écran ne la recalcule pas.
 */
export type CadenceRelance =
  | {
      type: 'CRENEAUX';
      heures: number[];
      joursSoutenus: number;
      periodeEspaceeJours: number;
      heureEspacee: number;
    }
  | { type: 'PERIODIQUE'; delaiHeures: number; periodeJours: number }
  | { type: 'AVANT'; jours: number; heure: number }
  | { type: 'APRES'; jours: number; heure: number };

export interface ScenarioRelance {
  code: string;
  libelle: string;
  /** Cadence en clair, énoncée par le serveur depuis le réglage. */
  declencheur: string;
  /** Destinataires en clair. */
  destinataire: string;
  /** Cible du scénario : « Candidat », « Apprenant » ou « Entreprise ». */
  cible: string;
  /** Origine de la cadence, avec son article (« l’inscription »). */
  evenement: string;
  destinataires: DestinatairesRelance;
  typeCadence: CadenceRelance['type'] | null;
  cadence: CadenceRelance | null;
  canaux: CanalNotification[];
  message: string;
  messageResponsable: string;
  variables: string[];
  variablesResponsable: string[];
  actif: boolean;
  sites: string[];
  calculable: boolean;
  cibles: number;
  /** Dossiers sans responsable en charge, adressés à l'administratif de l'antenne. */
  sansResponsable: number;
  envois: number;
}

export interface ReponseRelances {
  scenarios: ScenarioRelance[];
  derniersEnvois: {
    id: string;
    nom: string;
    canal: CanalNotification;
    pour: PourEnvoiRelance;
    destinataire: string | null;
    message: string | null;
    issue: IssueEnvoi;
    siteCode: string;
    creeLe: string;
  }[];
  compteurs: {
    envois30Jours: number;
    mails: number;
    sms: number;
    ciblesEnAttente: number;
    sansCoordonnees: number;
    sansResponsable: number;
  };
  expedition: EtatExpedition;
  variables: string[];
}

export function useRelances(site: string) {
  const parametres = new URLSearchParams({ site });
  return useQuery({
    queryKey: ['relances', site],
    queryFn: () => appelApi<ReponseRelances>(`/relances?${parametres.toString()}`),
  });
}

function invalider(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['relances'] });
}

export function useModifierScenario() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      code: string;
      actif?: boolean;
      destinataires?: DestinatairesRelance;
      cadence?: CadenceRelance;
      canaux?: CanalNotification[];
      message?: string;
      messageResponsable?: string;
      sites?: string[];
    }) => {
      const { code, ...corps } = variables;
      return appelApi<{ message: string }>(`/relances/scenarios/${code}`, {
        methode: 'PATCH',
        corps,
      });
    },
    onSuccess: () => invalider(client),
  });
}

export interface ResultatEnvoi {
  cible: CibleRelance;
  envoyees: number;
  /** Récapitulatifs adressés aux responsables. */
  recapitulatifs: number;
  ecartes: { nom: string; motif: string }[];
  /** Envois refusés par le prestataire : à distinguer d'un destinataire écarté. */
  echecs: { nom: string; motif: string }[];
  issue: IssueEnvoi;
  message: string;
}

export function useEnvoyerRelances() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      cible: CibleRelance;
      canaux: CanalNotification[];
      message?: string;
      site?: string;
    }) => appelApi<ResultatEnvoi>('/relances/envois', { methode: 'POST', corps: variables }),
    onSuccess: () => invalider(client),
  });
}
